// Form and ticket for tiered price-table categories (printing, CD/DVD, ...).

import { esc, attrs, options, usd } from "./html.js";
import { availableChoices, addonApplies } from "../engine/tiered.js";
import { piecesPerSheet, sheetFromLabel, bestOfferedUp } from "../engine/impose.js";

// Options cascade in declaration order: each menu is narrowed only by the
// menus above it, so changing "Disc type" re-filters "Packaging" and never
// the other way round.
const choicesFor = (cat, optionId, selections) => {
  const idx = cat.options.findIndex((o) => o.id === optionId);
  const prior = Object.fromEntries(cat.options.slice(0, idx).map((o) => [o.id, selections[o.id]]));
  return availableChoices(cat, optionId, prior);
};

export const newTieredState = (cat) => {
  const s = { selections: {}, quantity: "", addons: [], finished: { w: "", h: "" } };
  normalizeTiered(s, cat);
  // Start at the first price break so the ticket shows a real price on load.
  s.quantity = currentBreaks(cat, s.selections)[0]?.qty ?? "";
  return s;
};

/** Drop selections and add-ons that no longer fit after a change. */
export function normalizeTiered(s, cat) {
  for (const o of cat.options) {
    const avail = choicesFor(cat, o.id, s.selections);
    if (!avail.some((c) => c.id === s.selections[o.id])) s.selections[o.id] = avail[0]?.id ?? "";
  }
  s.addons = s.addons.filter((id) => {
    const a = (cat.addons ?? []).find((x) => x.id === id);
    return a && addonApplies(a, s.selections);
  });
}

function currentBreaks(cat, selections) {
  const t = cat.tables.find((t) => Object.entries(t.when).every(([k, v]) => selections[k] === v));
  return t ? [...t.breaks].sort((a, b) => a.qty - b.qty) : [];
}

/** Suggest the N-up choice from a finished piece size. */
export function suggestUp(s, cat) {
  const stock = cat.options.find((o) => o.id === "stock")?.choices.find((c) => c.id === s.selections.stock);
  const sheet = stock && sheetFromLabel(stock.label);
  const w = Number(s.finished?.w);
  const h = Number(s.finished?.h);
  if (!sheet || !(w > 0 && h > 0)) return null;
  const fits = piecesPerSheet(sheet, [w, h]);
  const offered = choicesFor(cat, "up", s.selections).map((c) => Number(c.id));
  return { sheet, fits, up: bestOfferedUp(fits, offered) };
}

function impositionHelper(s, cat, key) {
  const hint = suggestUp(s, cat);
  return `<div class="hint" style="display:flex;flex-wrap:wrap;gap:.5rem;align-items:end;margin-top:-.6rem">
    <span style="flex-basis:100%">Not sure about N-up? Enter your finished size and we'll pick it.</span>
    <label class="field" style="width:6.5rem">Width (in)<input type="number" min="0.5" step="0.125" data-path="t.${key}.finished.w" value="${esc(s.finished?.w)}"></label>
    <label class="field" style="width:6.5rem">Height (in)<input type="number" min="0.5" step="0.125" data-path="t.${key}.finished.h" value="${esc(s.finished?.h)}"></label>
    <button class="btn" data-action="apply-impose" data-key="${esc(key)}">Pick N-up</button>
    ${hint ? `<span style="flex-basis:100%">${hint.up ? `Fits ${hint.fits} per ${hint.sheet.join("×")}" sheet → <b>${hint.up}-up</b>.` : `That size doesn't fit this paper. Try a larger stock.`}</span>` : ""}
  </div>`;
}

export function tieredForm(s, cat, key) {
  const breaks = currentBreaks(cat, s.selections);
  const addons = (cat.addons ?? []).filter((a) => addonApplies(a, s.selections));
  return `
  <fieldset><legend><span class="num">01</span>Options</legend>
    <div class="row">${cat.options
      .map((o) => {
        const avail = choicesFor(cat, o.id, s.selections);
        return `<label class="field">${esc(o.label)}<select data-path="t.${key}.selections.${o.id}">${options(avail, s.selections[o.id])}</select></label>`;
      })
      .join("")}</div>
  </fieldset>

  ${cat.options.some((o) => o.id === "up") ? impositionHelper(s, cat, key) : ""}

  <fieldset><legend><span class="num">02</span>${esc(cat.quantityLabel ?? "Quantity")}</legend>
    <label class="field" style="max-width:220px">How many ${esc(cat.unitLabel ?? "pieces")}s?
      <input type="number" min="1" step="1" inputmode="numeric" data-path="t.${key}.quantity" data-type="int" value="${esc(s.quantity)}" placeholder="${breaks[0]?.qty ?? ""}"></label>
    ${breaks.length ? `<div class="qty-picks" role="group" aria-label="Common quantities">${breaks
      .map((b) => `<button data-action="set-qty" data-key="${esc(key)}" data-qty="${b.qty}" aria-pressed="${Number(s.quantity) === b.qty}">${b.qty.toLocaleString()}</button>`)
      .join("")}</div>` : ""}
  </fieldset>

  ${addons.length ? `<fieldset><legend><span class="num">03</span>Extras</legend>
    <div style="display:grid;gap:.45rem">${addons
      .map((a) => `<label class="check"><input type="checkbox" data-addon="${esc(a.id)}" data-key="${esc(key)}" ${attrs({ checked: s.addons.includes(a.id) })}>
        <span>${esc(a.label)} <span style="font-family:var(--nms-font-mono);color:var(--nms-navy)">${a.kind === "flat" ? usd(a.amount) : `${a.amount < 0 ? "−" : "+"}${usd(Math.abs(a.amount))} each`}</span></span></label>`)
      .join("")}</div>
  </fieldset>` : ""}`;
}

export function tieredTicket(q, s, cat, key) {
  const breaks = q.breaks ?? currentBreaks(cat, s.selections);
  const chosen = cat.options.map((o) => {
    const c = o.choices.find((c) => c.id === s.selections[o.id]);
    return c ? `<dt>${esc(o.label)}</dt><dd style="font-family:inherit">${esc(c.label)}</dd>` : "";
  });
  const currentQty = q.status === "priced" ? breaks.findLast((b) => b.qty <= q.quantity)?.qty : null;
  return `
    <dl>${chosen.join("")}</dl>
    ${breaks.length ? `<div><div class="group-title">Price breaks</div>
      <table class="breaks"><thead><tr><th>Qty</th><th>Each</th><th>Total</th></tr></thead><tbody>${breaks
        .map((b) => `<tr data-qty="${b.qty}" data-key="${esc(key)}" class="${b.qty === currentQty ? "current" : ""}" title="Use ${b.qty}"><td>${b.qty.toLocaleString()}</td><td>${usd(b.unit)}</td><td>${usd(b.total ?? b.unit * b.qty)}</td></tr>`)
        .join("")}</tbody></table></div>` : ""}
    ${q.status === "priced" ? `<div class="sep"></div><dl>
      <dt>${q.quantity.toLocaleString()} × ${usd(q.unit)}</dt><dd>${usd(q.itemsTotal)}</dd>
      ${q.addons.map((a) => `<dt>${esc(a.label)}</dt><dd class="${a.amount < 0 ? "neg" : ""}">${usd(a.amount)}</dd>`).join("")}
    </dl>
    ${q.upsell ? `<p class="hint">Order <b>${q.upsell.qty.toLocaleString()}</b> and pay ${usd(q.upsell.unit)} each.</p>` : ""}` : ""}`;
}
